import React, { useEffect, useState } from 'react'
import { Modal, ModalHeader, ModalBody, ModalFooter, FormGroup, Label, Input, Spinner } from 'reactstrap'
import { getPrimaryButtonClass, getPrimaryTextClass } from 'helpers/sf_utils'

// Requires a reason before a bulk disposition edit is submitted. The edit
// itself runs async (BulkEditJob); this only gates the confirm click.
//
// previewLoading/previewCount/previewLockHeld/previewError describe the
// POST /bulk_edits/preview request the parent fires as soon as this modal
// opens, so the user sees how many records are actually affected (and
// whether another bulk edit already holds the lock) before committing.
// isSubmitting reflects the real submit (POST /bulk_edits) in flight; the
// parent only closes this modal once that submit succeeds, so both buttons
// stay disabled for its whole duration rather than letting a second click
// (or a Cancel) race the request.
const BulkEditConfirmationModal = ({
    isOpen,
    description,
    onConfirm,
    onCancel,
    isSubmitting,
    previewLoading,
    previewCount,
    previewLockHeld,
    previewError,
    onRetryPreview,
}) => {
    const [reason, setReason] = useState('')

    // Covers both explicit Cancel and the parent closing this modal after a
    // successful submit -- either way the next bulk edit should start blank.
    useEffect(() => {
        if (!isOpen) setReason('')
    }, [isOpen])

    const handleCancel = () => {
        if (isSubmitting) return
        onCancel()
    }

    const hasValidPreview = typeof previewCount === 'number' && previewCount > 0
    const handleConfirm = () => {
        if (!reason.trim() || isSubmitting || previewLoading || previewLockHeld || previewError || !hasValidPreview) return
        onConfirm(reason.trim())
    }

    const confirmDisabled = !reason.trim() || isSubmitting || previewLoading || previewLockHeld || previewError || !hasValidPreview

    return (
        <Modal isOpen={isOpen} toggle={handleCancel} size={'lg'} centered={true}>
            <ModalHeader>
                <div className="d-flex align-items-center">
                    <i className={`label-title bx bx-edit-alt ${getPrimaryTextClass()} lh-1 me-2`}></i>
                    <span className="modal-title call-transcription-topic-heading lh-1">Confirm Bulk Edit</span>
                </div>
                <button
                    type="button"
                    className="close"
                    aria-label="Close"
                    disabled={isSubmitting}
                    onClick={handleCancel}
                >
                    <span aria-hidden="true">&times;</span>
                </button>
            </ModalHeader>
            <ModalBody className="bulk-edit-modal-body" style={{ overflowY: "auto" }}>
                <div className="bg-light border rounded p-3 mb-3">
                    <p className="mb-2 text-black">{description}</p>

                    {previewLoading && (
                        <p className="text-muted d-flex align-items-center gap-2 mb-0">
                            <Spinner size="sm" /> Checking how many records this will affect...
                        </p>
                    )}
                    {!previewLoading && previewLockHeld && (
                        <p className="text-danger d-flex align-items-start mb-0">
                            <i className="bx bx-error-circle font-size-16 me-2"></i>
                            Another bulk edit is already running against this data. Please wait for it to finish before starting a new one.
                        </p>
                    )}
                    {!previewLoading && !previewLockHeld && previewError && (
                        <div>
                            <p className="text-danger d-flex align-items-start mb-2">
                                <i className="bx bx-error-circle font-size-16 me-2"></i>
                                Couldn&apos;t verify how many records this would affect. Please try again.
                            </p>
                            <button type="button" className="btn btn-outline-secondary btn-sm" disabled={isSubmitting} onClick={onRetryPreview}>
                                Retry preview
                            </button>
                        </div>
                    )}
                    {!previewLoading && !previewLockHeld && !previewError && typeof previewCount === 'number' && (
                        previewCount > 0 ? (
                            <p className="d-flex align-items-start mb-0">
                                <i className={`bx bx-info-circle font-size-16 ${getPrimaryTextClass()} me-2`}></i>
                                <span>This will update <strong>{previewCount}</strong> matching record(s).</span>
                            </p>
                        ) : (
                            <p className="text-danger d-flex align-items-start mb-0">
                                <i className="bx bx-error-circle font-size-16 me-2"></i>
                                No matching records were found for this bulk edit.
                            </p>
                        )
                    )}
                </div>

                <FormGroup className="mb-0">
                    <Label className="font-weight-semibold">
                        Reason <span className="text-danger">*</span>
                    </Label>
                    <Input
                        type="textarea"
                        rows={3}
                        value={reason}
                        placeholder="Why are you making this change?"
                        onChange={(e) => setReason(e.target.value)}
                        disabled={isSubmitting}
                    />
                    {!reason.trim() && (
                        <small className="text-muted">A reason is required to proceed.</small>
                    )}
                </FormGroup>
            </ModalBody>
            <ModalFooter>
                <button
                    type="button"
                    className={`btn ${getPrimaryButtonClass()} ms-2`}
                    disabled={confirmDisabled}
                    onClick={handleConfirm}
                >
                    {isSubmitting ? 'Submitting...' : 'Confirm'}
                </button>
                <button type="button" className="btn btn-light ms-2" disabled={isSubmitting} onClick={handleCancel}>
                    Cancel
                </button>
            </ModalFooter>
        </Modal>
    )
}

export default BulkEditConfirmationModal
